import { useMemo, useState, type ReactNode, type CSSProperties } from 'react';
import { Icon } from './Icon';
import { cn } from '@/lib/cn';
import { useMediaQuery } from '@/lib/useMediaQuery';

/* ═══════════════════════════════════════════════════════════════════════════
 * DataTable — the ONE data table for the Automation Platform.
 *
 *   Consistent header · sortable columns · responsive column priorities ·
 *   footer with pagination + global stats · actions column · optional
 *   filters/stats slots · Lab.confirm ready — every surface that shows
 *   tabular run/test data MUST use it (see CLAUDE.md §0.2).
 *
 *   Two modes are supported :
 *     - paginated : footer shows page navigation + page-size selector
 *                   (50/100/200/500 max) + stats slot on the left.
 *     - simple    : fixed rows (no pagination). Caller can still supply
 *                   footer stats and a "see more" link to a dedicated
 *                   page — e.g. the dashboard's recent-campagnes card.
 * ═══════════════════════════════════════════════════════════════════════════ */

export interface DataTableColumn<T> {
  /** Stable identifier of the column (used for sort state, React keys). */
  key: string;
  /** Column header text. Pass '' for no header (e.g. actions column). */
  label: ReactNode;
  /** Cell renderer. */
  accessor: (row: T) => ReactNode;
  /** If true the header can be clicked to sort. Requires `sortValue`. */
  sortable?: boolean;
  /** Value used for sort comparison (string or number). */
  sortValue?: (row: T) => string | number | boolean | Date | null | undefined;
  /** Grid-template-columns fragment for this column. Default "minmax(0, 1fr)". */
  width?: string;
  /**
   * Responsive priority :
   *   1 = always visible
   *   2 = hidden below 1280 px
   *   3 = hidden below 1024 px
   *   4 = hidden below 768  px
   */
  priority?: 1 | 2 | 3 | 4;
  align?: 'left' | 'right' | 'center';
  /** Optional extra class on the cell. */
  cellClassName?: string;
  /** Monospace cell (font-variant-numeric: tabular-nums automatically). */
  mono?: boolean;
  /** Title text exposed as tooltip for truncated cells. */
  title?: (row: T) => string;
}

export type PageSize = 50 | 100 | 200 | 500;

export interface DataTableProps<T> {
  data: T[];
  columns: DataTableColumn<T>[];
  /** Stable row key — used for React keys + sort stability. */
  rowKey: (row: T) => string;

  /** Card title (rendered in the DS card header). */
  title?: ReactNode;
  /** Subtitle rendered next to the title (count, filter hint…). */
  subtitle?: ReactNode;

  /** Search input wired to the caller (client-side or server-side). */
  search?: {
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
  };
  /** Extra filter controls rendered on the same header row. */
  filters?: ReactNode;
  /** Stats / actions rendered on the right of the header row. */
  headerTrailing?: ReactNode;

  /** Left side of the footer — e.g. global counters. */
  footerLeading?: ReactNode;
  /** Right side of the footer — e.g. "see all" CTA. Hidden when paginated. */
  footerTrailing?: ReactNode;

  /** Pagination configuration. `false` or absent = fixed rows, no paging. */
  pagination?: boolean | {
    pageSize?: PageSize;
    pageSizes?: PageSize[];
  };

  /**
   * Initial sort state — `{ key, direction }`. When absent the table is
   * rendered in the order passed in `data`.
   */
  initialSort?: { key: string; direction: 'asc' | 'desc' };

  /** Row click handler. */
  onRowClick?: (row: T) => void;
  /** Selected / highlighted row predicate. */
  rowSelected?: (row: T) => boolean;

  /** Empty state element. Default = generic "aucun résultat". */
  empty?: ReactNode;
  /** Loading state element. */
  loading?: boolean;
  /** Optional fixed max height for the rows area (scrolls if exceeded). */
  maxRowsHeight?: number | string;
}

const PAGE_SIZE_DEFAULTS: PageSize[] = [50, 100, 200, 500];

/* ─── Component ────────────────────────────────────────────────────────── */

export function DataTable<T>({
  data,
  columns,
  rowKey,
  title,
  subtitle,
  search,
  filters,
  headerTrailing,
  footerLeading,
  footerTrailing,
  pagination,
  initialSort,
  onRowClick,
  rowSelected,
  empty,
  loading,
  maxRowsHeight,
}: DataTableProps<T>) {
  /* ── State ───────────────────────────────────────────────────────────── */
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(
    initialSort ? { key: initialSort.key, dir: initialSort.direction } : null,
  );
  const pagOpts = typeof pagination === 'object' ? pagination : null;
  const pagEnabled = Boolean(pagination);
  const pageSizes = pagOpts?.pageSizes ?? PAGE_SIZE_DEFAULTS;
  const [pageSize, setPageSize] = useState<PageSize>(pagOpts?.pageSize ?? pageSizes[1] ?? 100);
  const [page, setPage] = useState(1);

  /* ── Sort ────────────────────────────────────────────────────────────── */
  const sortedData = useMemo(() => {
    if (!sort) return data;
    const col = columns.find((c) => c.key === sort.key);
    if (!col || !col.sortable || !col.sortValue) return data;
    const mult = sort.dir === 'asc' ? 1 : -1;
    return [...data].sort((a, b) => {
      const va = col.sortValue!(a);
      const vb = col.sortValue!(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (va instanceof Date && vb instanceof Date) {
        return (va.getTime() - vb.getTime()) * mult;
      }
      if (typeof va === 'number' && typeof vb === 'number') {
        return (va - vb) * mult;
      }
      return String(va).localeCompare(String(vb), 'fr', { numeric: true }) * mult;
    });
  }, [data, columns, sort]);

  /* ── Pagination ──────────────────────────────────────────────────────── */
  const totalRows  = sortedData.length;
  const totalPages = pagEnabled ? Math.max(1, Math.ceil(totalRows / pageSize)) : 1;
  const clampedPage = Math.min(page, totalPages);
  const startIdx = pagEnabled ? (clampedPage - 1) * pageSize : 0;
  const endIdx   = pagEnabled ? Math.min(totalRows, startIdx + pageSize) : totalRows;
  const visible  = pagEnabled ? sortedData.slice(startIdx, endIdx) : sortedData;

  const toggleSort = (key: string) => {
    const col = columns.find((c) => c.key === key);
    if (!col?.sortable) return;
    setSort((cur) => {
      if (!cur || cur.key !== key) return { key, dir: 'asc' };
      if (cur.dir === 'asc') return { key, dir: 'desc' };
      return null; // cycle back to "no sort"
    });
  };

  // Sync avec les media-queries CSS .dt-pX (kit.css) :
  //   p2 caché < 1280  ·  p3 caché < 1024  ·  p4 caché < 768
  // On exclut les colonnes cachées du grid-template pour que la table
  // n'occupe que la largeur des colonnes RÉELLEMENT visibles (sinon les
  // tracks invisibles font déborder horizontalement sur mobile).
  const isUnder768 = useMediaQuery('(max-width: 767px)');
  const isUnder1024 = useMediaQuery('(max-width: 1023px)');
  const isUnder1280 = useMediaQuery('(max-width: 1279px)');
  const visibleColumns = useMemo(() => columns.filter((c) => {
    const p = c.priority ?? 1;
    if (p === 4 && isUnder768) return false;
    if (p === 3 && isUnder1024) return false;
    if (p === 2 && isUnder1280) return false;
    return true;
  }), [columns, isUnder768, isUnder1024, isUnder1280]);

  // Sur mobile (≤767px) on neutralise les min-width déclarés (`minmax(Xpx, ...)`)
  // pour autoriser la grille à se contracter au viewport. Sinon les colonnes
  // gardent leur min-width et la table déborde horizontalement.
  const gridTemplate = visibleColumns.map((c) => {
    const w = c.width ?? 'minmax(0, 1fr)';
    if (isUnder768 && w.startsWith('minmax(')) return 'minmax(0, 1fr)';
    return w;
  }).join(' ');

  /* ── Header (card header) ────────────────────────────────────────────── */
  const hasHeader = Boolean(title || subtitle || search || filters || headerTrailing);

  /* ── Empty / loading states ──────────────────────────────────────────── */
  const showEmpty  = !loading && visible.length === 0;
  const showRows   = !loading && visible.length > 0;

  return (
    <div className="dt">
      {hasHeader && (
        <header className="dt-header">
          {(title || subtitle) && (
            <div className="dt-title-wrap">
              {title && <h3 className="dt-title">{title}</h3>}
              {subtitle && <span className="dt-subtitle">{subtitle}</span>}
            </div>
          )}
          {search && (
            <label className="dt-search" aria-label={search.placeholder ?? 'Rechercher'}>
              <Icon name="search" size={12} />
              <input
                type="search"
                className="dt-search-input"
                placeholder={search.placeholder ?? 'Rechercher…'}
                value={search.value}
                onChange={(e) => search.onChange(e.target.value)}
              />
              {search.value && (
                <button
                  type="button"
                  className="dt-search-clear"
                  aria-label="Effacer la recherche"
                  onClick={() => search.onChange('')}
                >
                  <Icon name="x" size={10} />
                </button>
              )}
            </label>
          )}
          {filters}
          <div className="dt-header-trail">
            {headerTrailing}
          </div>
        </header>
      )}

      <div
        className="dt-rows-wrap"
        style={maxRowsHeight ? { maxHeight: maxRowsHeight, overflow: 'auto' } : undefined}
      >
        {/* Column headers */}
        <div
          className="dt-thead"
          role="rowgroup"
          style={{ '--dt-cols': gridTemplate } as CSSProperties}
        >
          <div className="dt-row dt-row-head" role="row">
            {visibleColumns.map((c) => (
              <div
                key={c.key}
                role="columnheader"
                className={cn(
                  'dt-cell dt-head-cell',
                  `dt-p${c.priority ?? 1}`,
                  c.align === 'right' && 'dt-right',
                  c.align === 'center' && 'dt-center',
                )}
                aria-sort={
                  sort?.key === c.key
                    ? sort.dir === 'asc' ? 'ascending' : 'descending'
                    : c.sortable ? 'none' : undefined
                }
                onClick={c.sortable ? () => toggleSort(c.key) : undefined}
                style={{ cursor: c.sortable ? 'pointer' : undefined }}
              >
                <span className="dt-head-label">{c.label}</span>
                {c.sortable && (
                  <span className="dt-sort-indicator" aria-hidden>
                    {sort?.key === c.key ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="dt-tbody" role="rowgroup" style={{ '--dt-cols': gridTemplate } as CSSProperties}>
          {loading && (
            <div className="dt-empty">
              <span className="mono" style={{ fontSize: 10, letterSpacing: '0.1em' }}>CHARGEMENT…</span>
            </div>
          )}

          {showEmpty && (
            <div className="dt-empty grid-bg">
              {empty ?? (
                <>
                  <div className="mono" style={{ fontSize: 10, letterSpacing: '0.1em', marginBottom: 6 }}>
                    ∅ AUCUN RÉSULTAT
                  </div>
                  <p style={{ fontSize: 13, margin: 0 }}>Aucune ligne ne correspond à ces critères.</p>
                </>
              )}
            </div>
          )}

          {showRows && visible.map((row) => {
            const selected = rowSelected?.(row);
            return (
              <div
                key={rowKey(row)}
                role="row"
                className={cn('dt-row', selected && 'dt-row-selected', onRowClick && 'dt-row-clickable')}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                onKeyDown={onRowClick ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onRowClick(row);
                  }
                } : undefined}
              >
                {visibleColumns.map((c) => (
                  <div
                    key={c.key}
                    role="cell"
                    className={cn(
                      'dt-cell',
                      `dt-p${c.priority ?? 1}`,
                      c.align === 'right' && 'dt-right',
                      c.align === 'center' && 'dt-center',
                      c.mono && 'mono',
                      c.cellClassName,
                    )}
                    title={c.title ? c.title(row) : undefined}
                  >
                    {c.accessor(row)}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      {(footerLeading || footerTrailing || pagEnabled) && (
        <footer className="dt-footer">
          <div className="dt-footer-leading">{footerLeading}</div>
          <div className="dt-footer-spacer" />
          {pagEnabled ? (
            <Pagination
              totalRows={totalRows}
              startIdx={startIdx}
              endIdx={endIdx}
              page={clampedPage}
              totalPages={totalPages}
              pageSize={pageSize}
              pageSizes={pageSizes}
              onPage={setPage}
              onPageSize={(s) => { setPageSize(s); setPage(1); }}
            />
          ) : (
            <div className="dt-footer-trailing">{footerTrailing}</div>
          )}
        </footer>
      )}
    </div>
  );
}

/* ─── Pagination sub-component ───────────────────────────────────────── */

function Pagination({
  totalRows, startIdx, endIdx, page, totalPages, pageSize, pageSizes,
  onPage, onPageSize,
}: {
  totalRows: number;
  startIdx: number;
  endIdx: number;
  page: number;
  totalPages: number;
  pageSize: PageSize;
  pageSizes: PageSize[];
  onPage: (p: number) => void;
  onPageSize: (s: PageSize) => void;
}) {
  const disableFirst = page <= 1;
  const disableLast  = page >= totalPages;
  return (
    <div className="dt-pagination">
      <span className="dt-page-size-label">Lignes</span>
      <select
        className="dt-page-size"
        value={pageSize}
        onChange={(e) => onPageSize(Number(e.target.value) as PageSize)}
        aria-label="Lignes par page"
      >
        {pageSizes.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <span className="dt-page-range mono">
        {totalRows === 0 ? '0' : `${startIdx + 1}–${endIdx}`} / {totalRows}
      </span>
      <div className="dt-page-nav">
        <button type="button" aria-label="Première page"    disabled={disableFirst} onClick={() => onPage(1)}>«</button>
        <button type="button" aria-label="Page précédente"  disabled={disableFirst} onClick={() => onPage(page - 1)}>‹</button>
        <span className="dt-page-number mono">{page} / {totalPages}</span>
        <button type="button" aria-label="Page suivante"    disabled={disableLast}  onClick={() => onPage(page + 1)}>›</button>
        <button type="button" aria-label="Dernière page"    disabled={disableLast}  onClick={() => onPage(totalPages)}>»</button>
      </div>
    </div>
  );
}
